/**
 * Note: A cookie-based approach will not work for iFrame across all browsers,
 * as third-party cookies are being phased out. Since the major browsers have not yet agreed on
 * a standardized method for handling cookies in iFrames, any viable solution will require
 * architectural changes—likely involving server-side cookie setting with secure,
 * HTTP-only, partitioned cookies that include expiry details:
 *
 *   • Chrome: https://developer.mozilla.org/en-US/docs/Web/Privacy/Guides/Privacy_sandbox/Partitioned_cookies
 *              https://developers.google.com/privacy-sandbox/cookies/chips
 *   • Firefox: https://developer.mozilla.org/en-US/docs/Web/Privacy/Guides/State_Partitioning
 *   • Safari:  https://github.com/privacycg/storage-access/issues/75
 *
 * Cookies offer the advantage of working seamlessly across subdomains, which might make a
 * cookie-only solution preferable once a consensus is reached—especially considering Safari
 * has yet to provide a solution for cookies. Until then, localStorage serves as a hopefully reliable fallback for iFrames.
 *
 * Using secure HTTP-only cookies can also enhance security in shadow DOM embed contexts by
 * preventing unauthorized first party script access of the cookies — an important consideration for especially protecting the
 * frame_session cookie and maintaining strong security isolation.
 *
 * Another considerations is that using HTTP-only cookies might help us to better enforce the CORS policy defined by our backend.
 * Malicious Site -> Bad Actor Backend -> Our API. A bad actor could still though intercept every request.
 */

import { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import log from "./log";

interface StorageOptions {
  expires?: number;
  domain?: string;
  path?: string;
}

interface StorageValue {
  value: string;
  expires?: number; // timestamp
}

export interface MustFrameStorage {
  deviceId: string;
  userId: string;
  sessionId?: string;
}

export interface FrameStorage extends Partial<MustFrameStorage> {}

const COOKIE_PREFIX = "botbrains";
const DEFAULT_LONGTERM_EXPIRY = 180; // 6 months in days
const DEFAULT_SESSION_EXPIRY = 7; // 1 week in days
const TOMBSTONE_USER_ID = "00000000-dead-beef-cafe-000000000000";

// NOTE(memben): in case setting the cookie fails use the full reference:
//               https://publicsuffix.org/list/public_suffix_list.dat
// Common public suffixes we care about
const PUBLIC_SUFFIXES = new Set([
  "ts.net", // Tailscale domains
  "co.uk", // UK
  "co.jp", // Japan
  "com.au", // Australia
  "com.br", // Brazil
  "com.cn", // China
  "herokuapp.com",
  "github.io",
  "vercel.app",
  "netlify.app",
]);

function determineRootDomain(): string {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  // If hostname has no dots or is an IP (IPv4 or IPv6), return as-is
  if (parts.length <= 1 || hostname.includes(":")) {
    return hostname;
  }

  const lastTwoParts = parts.slice(-2).join(".");
  if (PUBLIC_SUFFIXES.has(lastTwoParts)) {
    // For domains like example.ts.net, return .example.ts.net
    return parts.length > 2 ? "." + parts.slice(-3).join(".") : hostname;
  }

  // Standard case: return .domain.tld for subdomain.domain.tld
  return parts.length > 2 ? "." + parts.slice(-2).join(".") : hostname;
}

function getStorageOptions(expires?: number): StorageOptions {
  return {
    expires,
    path: "/",
    // TODO(memben): use state from redux store
    domain: determineRootDomain(),
  };
}

// NOTE(memben): Partioned in embeded context https://developers.google.com/privacy-sandbox/cookies/storage-partitioning
class LocalStorageFallback {
  private prefix = `${COOKIE_PREFIX}_storage_`;

  private getKey(key: string): string {
    return this.prefix + key;
  }

  get(key: string): string | undefined {
    try {
      const item = localStorage.getItem(this.getKey(key));
      if (!item) return undefined;

      const parsed = JSON.parse(item) as StorageValue;

      // Check expiration
      if (parsed.expires && Date.now() > parsed.expires) {
        this.remove(key);
        return undefined;
      }

      return parsed.value;
    } catch {
      return undefined;
    }
  }

  set(key: string, value: string, options?: StorageOptions): void {
    try {
      const storageValue: StorageValue = {
        value,
        expires: options?.expires
          ? Date.now() + options.expires * 24 * 60 * 60 * 1000
          : undefined,
      };
      localStorage.setItem(this.getKey(key), JSON.stringify(storageValue));
    } catch (error) {
      console.warn("Failed to set localStorage item:", error);
    }
  }

  remove(key: string): void {
    try {
      localStorage.removeItem(this.getKey(key));
    } catch (error) {
      console.warn("Failed to remove localStorage item:", error);
    }
  }

  cleanup(): void {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(this.prefix))
        .forEach((key) => {
          const item = localStorage.getItem(key);
          if (!item) return;

          const parsed = JSON.parse(item) as StorageValue;
          if (parsed.expires && Date.now() > parsed.expires) {
            localStorage.removeItem(key);
          }
        });
    } catch (error) {
      console.warn("Failed to cleanup localStorage:", error);
    }
  }
}

// Storage manager that handles both cookie and localStorage
class StorageManager {
  private localStorageFallback: LocalStorageFallback;
  private useFallback: boolean;

  constructor() {
    this.localStorageFallback = new LocalStorageFallback();
    this.useFallback = this.areCookiesBlocked();

    log.debug(`Using ${this.useFallback ? "localStorage" : "cookies"} storage`);

    // Periodically clean up expired localStorage items
    // NOTE(memben): should not be necessary - but keeping a best effort to honor user data retention
    if (this.useFallback) {
      setInterval(() => this.localStorageFallback.cleanup(), 60000); // every minute
    }
  }

  private areCookiesBlocked(): boolean {
    const inIframe = window.self !== window.top; // https://stackoverflow.com/questions/326069
    if (inIframe) return true; // third party cookies are blocked in iframes

    // Check if cookies are available (could not be in case of public suffixes)
    try {
      const options = getStorageOptions();
      Cookies.set("botbrains_test", "test", options);
      const testCookie = Cookies.get("botbrains_test");
      Cookies.remove("botbrains_test");
      return !testCookie;
    } catch {
      return true;
    }
  }

  get(key: string): string | undefined {
    return this.useFallback
      ? this.localStorageFallback.get(key)
      : Cookies.get(key);
  }

  set(key: string, value: string, options?: StorageOptions): void {
    if (this.useFallback) {
      this.localStorageFallback.set(key, value, options);
    } else {
      Cookies.set(key, value, options);
    }
  }

  remove(key: string, options?: StorageOptions): void {
    if (this.useFallback) {
      this.localStorageFallback.remove(key);
    } else {
      Cookies.remove(key, options);
    }
  }
}

// Singleton storage manager
const storageManager = new StorageManager();

function getFrameStorageKeys(frameId: string) {
  return {
    device: `${COOKIE_PREFIX}-device-${frameId}`,
    user: `${COOKIE_PREFIX}-user-${frameId}`,
    session: `${COOKIE_PREFIX}-session-${frameId}`,
  };
}

export function getFrameStorage(frameId: string): MustFrameStorage {
  const keys = getFrameStorageKeys(frameId);
  return {
    deviceId: storageManager.get(keys.device) || uuidv4(),
    // TODO(memben): make user id optional and remove TOMEBSTONE_USER_ID
    userId: storageManager.get(keys.user) || TOMBSTONE_USER_ID, // NOTE(memben): Temporary uuid, will be overwritten by backend,
    sessionId: storageManager.get(keys.session),
  };
}

export function setFrameStorage(
  frameId: string | undefined,
  storage: FrameStorage
): void {
  if (!frameId) return;
  const keys = getFrameStorageKeys(frameId);

  if (storage.deviceId) {
    storageManager.set(
      keys.device,
      storage.deviceId,
      getStorageOptions(DEFAULT_LONGTERM_EXPIRY)
    );
  }

  if (storage.userId) {
    storageManager.set(
      keys.user,
      storage.userId,
      getStorageOptions(DEFAULT_LONGTERM_EXPIRY)
    );
  }

  if (storage.sessionId) {
    storageManager.set(
      keys.session,
      storage.sessionId,
      getStorageOptions(DEFAULT_SESSION_EXPIRY)
    );
  }
}

export function clearStorage(
  frameId: string,
  {
    deviceId,
    sessionId,
    userId,
  }: { deviceId?: boolean; sessionId?: boolean; userId?: boolean }
): void {
  const keys = getFrameStorageKeys(frameId);
  const options = getStorageOptions();

  if (deviceId) storageManager.remove(keys.device, options);
  if (sessionId) storageManager.remove(keys.session, options);
  if (userId) storageManager.remove(keys.user, options);
}

// NOTE(memben): active polling - "listening" for changes in storage
export function useFrameStorage(frameId: string): MustFrameStorage {
  const [frameStorage, setStorage] = useState<MustFrameStorage>(() =>
    getFrameStorage(frameId)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setStorage(getFrameStorage(frameId));
    }, 1000);

    return () => clearInterval(interval);
  }, [frameId]);

  return frameStorage;
}

// Simple localStorage wrapper for caching with TTL support
export const ls = {
  /**
   * Check if localStorage is available and accessible
   */
  haveAccess(): boolean {
    try {
      const testKey = "__ls_test__";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Set a value in localStorage with optional TTL (in seconds)
   * @param key - The key to store
   * @param value - The value to store (will be JSON stringified)
   * @param ttl - Time to live in seconds (optional)
   */
  setex<T>(key: string, value: T, ttl?: number): boolean {
    if (!this.haveAccess()) {
      return false;
    }

    try {
      const item = {
        value,
        expiry: ttl ? Date.now() + ttl * 1000 : undefined,
      };
      localStorage.setItem(key, JSON.stringify(item));
      return true;
    } catch (error) {
      // Handle quota exceeded or other errors
      console.warn("[botbrains] Failed to set localStorage item:", error);
      return false;
    }
  },

  /**
   * Get a value from localStorage
   * @param key - The key to retrieve
   * @returns The value if found and not expired, null otherwise
   */
  get<T>(key: string): T | null {
    if (!this.haveAccess()) {
      return null;
    }

    try {
      const item = localStorage.getItem(key);
      if (!item) {
        return null;
      }

      const parsed = JSON.parse(item) as { value: T; expiry?: number };

      // Check if expired
      if (parsed.expiry && Date.now() > parsed.expiry) {
        localStorage.removeItem(key);
        return null;
      }

      return parsed.value;
    } catch (error) {
      // Handle parse errors or access errors
      console.warn("[botbrains] Failed to get localStorage item:", error);
      // Clean up corrupted item
      try {
        localStorage.removeItem(key);
      } catch {}
      return null;
    }
  },

  /**
   * Remove a value from localStorage
   * @param key - The key to remove
   */
  remove(key: string): boolean {
    if (!this.haveAccess()) {
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  },
};
